import React, { useState } from 'react';
import { Sparkle, Coin, Check, CaretDown, ArrowRight, Crown, Lock, MagnifyingGlass, PaperPlaneTilt, HouseLine } from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { cn, getSession } from '../lib/utils';
import { CREDIT_PACKS, AGENT_PRO_PLAN, IS_BETA, BETA_FREE_CREDITS } from '../lib/constants';
import { getAgentProWaitlistEmail, setAgentProWaitlistEmail } from '../lib/credits';
interface PricingProps {
  navigate: (page: string, context?: any) => void;
}
type Persona = 'proprietar' | 'agent';
export function Pricing({
  navigate
}: PricingProps) {
  const session = getSession();
  const defaultPersona: Persona = session?.subRole === 'agent' ? 'agent' : 'proprietar';
  const [persona, setPersona] = useState<Persona>(defaultPersona);
  const [toast, setToast] = useState<string | null>(null);
  const [waitlistEmail, setWaitlistEmail] = useState<string>(() => getAgentProWaitlistEmail() || session?.email || '');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState<boolean>(() => !!getAgentProWaitlistEmail());
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };
  const handleWaitlist = (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail.trim()) return;
    setAgentProWaitlistEmail(waitlistEmail.trim());
    setWaitlistSubmitted(true);
    showToast('Te anunțăm primii când lansăm Agent PRO.');
  };
  const whyIzioPoints = [{
    Icon: MagnifyingGlass,
    title: 'Găsește chiriașul ideal',
    body: 'Explorează profilurile chiriașilor. Filtrează după buget, zonă și stil de viață pentru a găsi potrivirea perfectă.'
  }, {
    Icon: PaperPlaneTilt,
    title: 'Trimite o ofertă',
    body: 'Contactează direct chiriașii care îți plac. Trimite-le detalii despre proprietatea ta printr-o ofertă personalizată.'
  }, {
    Icon: HouseLine,
    title: 'Închiriază rapid',
    body: 'Programează vizionări doar cu persoane interesate și verificate. Închide contractul mai repede și mai sigur.'
  }];
  return <div className="min-h-screen bg-dark-1" data-id="element-3781">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-20" data-id="element-3782">
        {/* Hero — no CTA (post-signup context) */}
        <section className="text-center max-w-2xl mx-auto mb-10" data-id="element-3783">
          <h1 className="text-3xl sm:text-5xl font-bold text-light-1 tracking-tight leading-[1.05]" data-id="element-3784">
            Găsește chiriașul perfect
          </h1>

          <p className="mt-5 text-base sm:text-lg text-light-2 leading-relaxed" data-id="element-3785">
            Vrem să eliminăm îndoiala, nesiguranța și opacitatea din procesul de
            închiriere. Fie că ești agent sau proprietar, pe Izio găsești
            chiriași serioși și verificați.
          </p>

          {IS_BETA && <div className="mt-6 inline-flex items-start gap-3 bg-lime-5 border border-lime-4 rounded-2xl px-4 sm:px-5 py-3 text-left mx-auto" data-id="element-3786">
              <Sparkle size={18} weight="fill" className="text-lime-1 shrink-0 mt-0.5" data-id="element-3787" />
              <p className="text-sm text-light-1" data-id="element-3788">
                Platforma este în faza de testare. Ai{' '}
                <span className="text-lime-1 font-semibold" data-id="element-3789">
                  {BETA_FREE_CREDITS} credite gratuite
                </span>{' '}
                din partea noastră.
              </p>
            </div>}
        </section>

        {/* Persona toggle — mobile/tablet only */}
        <section className="lg:hidden mb-8 max-w-md mx-auto" data-id="element-3790">
          <div className="bg-dark-3 border border-dark-4 rounded-full p-1 grid grid-cols-2 gap-1" data-id="element-3791">
            {([{
            value: 'proprietar',
            label: 'Sunt proprietar'
          }, {
            value: 'agent',
            label: 'Sunt agent'
          }] as const).map(opt => <button key={opt.value} type="button" onClick={() => setPersona(opt.value)} className={cn('h-10 rounded-full text-sm font-medium transition-colors', persona === opt.value ? 'bg-dark-4 text-light-1' : 'text-light-3')} aria-pressed={persona === opt.value} data-id="element-3792">
                {opt.label}
              </button>)}
          </div>
        </section>

        {/* Packages section header */}
        <section className={cn('mb-6', persona === 'agent' && 'hidden lg:block')} data-id="element-3793">
          <h2 className="text-xl sm:text-2xl font-semibold text-light-1 mb-1" data-id="element-3794">
            Pachete de credite
          </h2>
          <p className="text-sm text-light-3" data-id="element-3795">
            Plată unică, fără abonament. Folosește creditele când îți apare un
            chiriaș potrivit.
          </p>
        </section>

        {/* PAYG cards */}
        <section className={cn('mb-14', persona === 'agent' && 'hidden lg:block')} data-id="element-3796">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3797">
            {CREDIT_PACKS.map(pack => {
            const pricePerCredit = (pack.priceEUR / pack.credits).toFixed(2);
            return <div key={pack.id} className={cn('relative rounded-2xl border bg-dark-2 p-6 flex flex-col transition-all', pack.recommended ? 'border-lime-1 shadow-[0_0_0_1px_rgba(205,245,61,0.3)] md:scale-[1.02]' : 'border-dark-4')} data-id="element-3798">
                  {pack.recommended && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-lime-1 text-dark-1 text-xs font-bold uppercase tracking-wider" data-id="element-3799">
                      Recomandat
                    </span>}

                  <h3 className="text-xl font-semibold text-light-1" data-id="element-3800">
                    {pack.name}
                  </h3>
                  <p className="text-sm text-light-3 mt-1" data-id="element-3801">{pack.tagline}</p>

                  <div className="mt-6 flex items-baseline gap-1.5" data-id="element-3802">
                    <span className="text-4xl font-bold text-light-1" data-id="element-3803">
                      {pack.priceEUR}
                    </span>
                    <span className="text-lg text-light-3" data-id="element-3804">€</span>
                  </div>
                  <p className="text-sm text-light-3 mt-1" data-id="element-3805">
                    <span className="text-light-1 font-semibold" data-id="element-3806">
                      {pack.credits} credite
                    </span>{' '}
                    · {pricePerCredit} €/credit
                  </p>

                  <ul className="mt-6 space-y-2.5 text-sm flex-1" data-id="element-3807">
                    {['1 credit = 1 ofertă trimisă', 'Creditele nu expiră niciodată', 'Plată unică, fără abonament', 'Refund automat pentru oferte necitite (în curând)'].map(feat => <li key={feat} className="flex items-start gap-2" data-id="element-3808">
                        <Check size={16} weight="bold" className="text-lime-1 shrink-0 mt-0.5" data-id="element-3809" />
                        <span className="text-light-2" data-id="element-3810">{feat}</span>
                      </li>)}
                  </ul>

                  {/* Coming-soon button — consistent disabled treatment across all 3 tiers */}
                  <button type="button" disabled aria-disabled="true" className="mt-6 h-11 rounded-md bg-dark-3 border border-dark-4 text-light-3 font-semibold text-sm inline-flex items-center justify-center gap-1.5 cursor-not-allowed" data-id="element-3811">
                    <Lock size={14} weight="fill" data-id="element-3812" />
                    În curând
                  </button>
                </div>;
          })}
          </div>
        </section>

        {/* Agent PRO — Coming soon */}
        <section className={cn('mb-14', persona === 'proprietar' && 'hidden lg:block')} data-id="element-3813">
          <h2 className="text-xl sm:text-2xl font-semibold text-light-1 mb-1" data-id="element-3814">
            Pentru agenții imobiliari
          </h2>
          <p className="text-sm text-light-3 mb-6" data-id="element-3815">
            Abonament dedicat cu beneficii speciale. Lansare în curând.
          </p>

          <div className="relative rounded-2xl border-2 border-lime-1/60 bg-dark-2 p-6 sm:p-8 overflow-hidden" data-id="element-3816">
            <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lime-5 border border-lime-4 text-lime-1 text-[11px] font-bold uppercase tracking-wider" data-id="element-3817">
              <Lock size={12} weight="fill" data-id="element-3818" />
              În curând
            </span>

            <div className="flex items-start gap-3 mb-4" data-id="element-3819">
              <div className="w-12 h-12 rounded-xl bg-lime-5 border border-lime-4 flex items-center justify-center shrink-0" data-id="element-3820">
                <Crown size={24} weight="fill" className="text-lime-1" data-id="element-3821" />
              </div>
              <div data-id="element-3822">
                <h3 className="text-2xl font-semibold text-light-1" data-id="element-3823">
                  {AGENT_PRO_PLAN.name}
                </h3>
                <p className="text-sm text-light-3" data-id="element-3824">
                  Pentru agenții imobiliari
                </p>
              </div>
            </div>

            <div className="flex items-baseline gap-1.5 mb-1" data-id="element-3825">
              <span className="text-4xl font-bold text-light-1" data-id="element-3826">
                {AGENT_PRO_PLAN.pricePerMonth}
              </span>
              <span className="text-lg text-light-3" data-id="element-3827">€ / lună</span>
            </div>
            <p className="text-sm text-light-3 mb-6" data-id="element-3828">
              <span className="text-light-1 font-semibold" data-id="element-3829">
                {AGENT_PRO_PLAN.creditsPerMonth} credite incluse
              </span>{' '}
              în fiecare lună
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm mb-6" data-id="element-3830">
              {AGENT_PRO_PLAN.features.map(feat => <li key={feat} className="flex items-start gap-2" data-id="element-3831">
                  <Check size={16} weight="bold" className="text-lime-1 shrink-0 mt-0.5" data-id="element-3832" />
                  <span className="text-light-2" data-id="element-3833">{feat}</span>
                </li>)}
            </ul>

            {waitlistSubmitted ? <div className="bg-lime-5 border border-lime-4 rounded-lg px-4 py-3 inline-flex items-center gap-2" data-id="element-3834">
                <Check size={16} weight="bold" className="text-lime-1" data-id="element-3835" />
                <p className="text-sm text-light-1" data-id="element-3836">
                  Ești pe lista de așteptare. Te anunțăm primii!
                </p>
              </div> : <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-2 sm:items-stretch max-w-md" data-id="element-3837">
                <Input type="email" placeholder="adresa@email.com" value={waitlistEmail} onChange={e => setWaitlistEmail(e.target.value)} required className="bg-dark-3 border-dark-4 text-light-1 placeholder:text-light-4 focus-visible:ring-lime-1 h-11" data-id="element-3838" />
                <Button type="submit" className="bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold h-11 px-5 whitespace-nowrap" data-id="element-3839">
                  Anunță-mă
                </Button>
              </form>}
          </div>
        </section>

        {/* De ce Izio — 3 proprietar steps */}
        <section className="mb-14" data-id="element-3840">
          <h2 className="text-xl sm:text-2xl font-semibold text-light-1 mb-1" data-id="element-3841">
            De ce Izio
          </h2>
          <p className="text-sm text-light-3 mb-6" data-id="element-3842">
            Trei pași simpli pentru a închiria mai repede și mai sigur.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3843">
            {whyIzioPoints.map(({
            Icon,
            title,
            body
          }, i) => <div key={title} className="rounded-2xl border border-dark-4 bg-dark-2 p-5" data-id="element-3844">
                <div className="flex items-center gap-3 mb-3" data-id="element-3845">
                  <div className="w-9 h-9 rounded-lg bg-lime-5 border border-lime-4 flex items-center justify-center shrink-0" data-id="element-3846">
                    <Icon size={18} weight="regular" className="text-lime-1" data-id="element-3847" />
                  </div>
                  <span className="text-xs font-bold text-light-3 tabular-nums" data-id="element-3848">
                    PASUL {i + 1}
                  </span>
                </div>
                <p className="font-semibold text-light-1" data-id="element-3849">{title}</p>
                <p className="text-sm text-light-3 mt-1.5 leading-relaxed" data-id="element-3850">
                  {body}
                </p>
              </div>)}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-4" data-id="element-3851">
          <h2 className="text-xl sm:text-2xl font-semibold text-light-1 mb-1" data-id="element-3852">
            Întrebări frecvente
          </h2>
          <p className="text-sm text-light-3 mb-6" data-id="element-3853">
            Răspunsuri scurte la ce întreabă cei mai mulți proprietari.
          </p>

          <div className="space-y-2" data-id="element-3854">
            {[{
            q: 'Ce este un credit?',
            a: 'O monedă internă Izio. 1 credit = o ofertă trimisă către un chiriaș. Nu se aplică alte comisioane.'
          }, {
            q: 'Chiriașii plătesc ceva?',
            a: 'Nu. Izio e 100% gratuit pentru chiriași, ca să ai acces la cea mai largă și calitativă bază de cereri active.'
          }, {
            q: 'Expiră creditele?',
            a: 'Nu. Creditele nu expiră niciodată. Le poți folosi când îți apare un chiriaș potrivit, oricând în viitor.'
          }, {
            q: 'Cum funcționează refund-ul pentru oferte necitite?',
            a: 'Feature programat pentru lansarea oficială. Dacă oferta ta nu e deschisă în 7 zile, creditul îți va fi returnat automat.'
          }, {
            q: 'Sunt agenție cu mai mulți agenți. Aveți planuri custom?',
            a: 'Da. Scrie-ne la contact@izio.ro și aranjăm un pachet cu locuri multiple pentru echipa ta.'
          }].map((item, i) => {
            const open = openFaq === i;
            return <div key={i} className="rounded-2xl border border-dark-4 bg-dark-2 overflow-hidden" data-id="element-3855">
                  <button type="button" onClick={() => setOpenFaq(open ? null : i)} className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-dark-3/40 transition-colors" aria-expanded={open} data-id="element-3856">
                    <span className="text-sm sm:text-base font-medium text-light-1" data-id="element-3857">
                      {item.q}
                    </span>
                    <CaretDown size={18} weight="bold" className={cn('text-light-3 shrink-0 transition-transform', open && 'rotate-180')} data-id="element-3858" />
                  </button>
                  {open && <div className="px-5 pb-5 -mt-1 text-sm text-light-2 leading-relaxed" data-id="element-3859">
                      {item.a}
                    </div>}
                </div>;
          })}
          </div>
        </section>
      </div>

      {/* Toast */}
      {toast && <div role="status" aria-live="polite" className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] max-w-md w-[calc(100%-2rem)] bg-dark-3 border border-dark-4 rounded-xl shadow-2xl px-4 py-3 text-sm text-light-1 animate-in fade-in slide-in-from-bottom-4 duration-200" data-id="element-3860">
          <div className="flex items-start gap-2" data-id="element-3861">
            <Sparkle size={16} weight="fill" className="text-lime-1 shrink-0 mt-0.5" data-id="element-3862" />
            <span data-id="element-3863">{toast}</span>
          </div>
        </div>}
    </div>;
}